/**
 * promptHasher — AEGIS-8 provenance chain.
 *
 * Deterministic SHA-256 hash of an AEGIS prompt or prompt+context envelope.
 * Used so provenance records can prove which exact prompt produced a given
 * decisionId without storing the prompt verbatim (PII / data-classification
 * concern for some tenant contexts).
 *
 * Browser path uses Web Crypto (SubtleCrypto.digest). Node test path uses
 * the built-in `crypto` module via dynamic require. Both produce identical
 * hex digests for identical inputs.
 *
 * Output: 64-char lowercase hex string prefixed with `sha256:`.
 */

const HASH_PREFIX = 'sha256:';

function _bytesToHex(bytes) {
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}

async function _hashViaSubtle(input) {
  const enc = new TextEncoder();
  const data = enc.encode(input);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
  return _bytesToHex(new Uint8Array(digest));
}

function _hashViaNode(input) {
  // Lazy require — never reached in browser builds.
  // eslint-disable-next-line global-require
  const nodeCrypto = require('node:crypto');
  return nodeCrypto.createHash('sha256').update(input, 'utf8').digest('hex');
}

/**
 * Hash an AEGIS prompt deterministically.
 *
 * Accepts either a string (raw prompt) or an object envelope. For envelopes,
 * we stringify with a stable key order so {a:1,b:2} and {b:2,a:1} hash to
 * the same value.
 *
 * @param {string|object|null|undefined} input
 * @returns {Promise<string>} `sha256:<hex>` (or `sha256:empty` for null/undefined/'')
 */
export async function hashPrompt(input) {
  if (input === null || input === undefined || input === '') {
    return `${HASH_PREFIX}empty`;
  }

  let canonical;
  if (typeof input === 'string') {
    canonical = input;
  } else if (typeof input === 'object') {
    canonical = _stableStringify(input);
  } else {
    canonical = String(input);
  }

  if (typeof globalThis.crypto !== 'undefined' && globalThis.crypto.subtle) {
    const hex = await _hashViaSubtle(canonical);
    return `${HASH_PREFIX}${hex}`;
  }
  return `${HASH_PREFIX}${_hashViaNode(canonical)}`;
}

/**
 * Stable JSON.stringify — sorts object keys recursively so logically-equal
 * objects produce identical canonical forms. Arrays preserve order.
 */
function _stableStringify(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return '[' + value.map(_stableStringify).join(',') + ']';
  }
  const keys = Object.keys(value).sort();
  const parts = keys.map(k => JSON.stringify(k) + ':' + _stableStringify(value[k]));
  return '{' + parts.join(',') + '}';
}

export default hashPrompt;
export const PROMPT_HASH_PREFIX = HASH_PREFIX;
